/* eslint-disable @typescript-eslint/ban-types */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import {
    StudyPayloadModel,
    criteriaModel,
    exportHeatmapResponseModel,
    fetchDocModel,
    fetchDraftResponseModel,
    generateDraftPayloadType,
    generateDraftResponseModel,
    generateHeatmapResponseModel,
    instructionsPromptResponseData,
    regenerateHeatmapOnReassignmentPayloadModel,
    regenerateHeatmapPayloadModel,
    regenerateHeatmapResponseModel,
    resetDraftResponseModel,
    resetECPromptInstructionResponseModel,
    restoreOriginalHeatmapResponseModel,
    saveDraftPayloadModel,
    saveDraftResponseModel,
    saveHeatmapPayloadType,
    saveInstructionLibraryPayloadModel,
    savePromptInstructionLibraryDataResponseModel,
    savePromptInstructionPayloadModel,
    saveWorkbenchInputStudyResponseModel,
    uploadPdfModel,
    DeleteECBCommentByIdPayload,
    EditECBCommentPayload,
    SaveECBCommentPayload,
    ResolveECBCommentPayload,
} from 'src/app/model/ecb-workbench.model';
import { apiConstant } from 'src/app/utility/constant/api-constant';
import { buildQueryString } from 'src/app/utility/helpers';

@Injectable({
    providedIn: 'root',
})
export class WorkbenchService {
    public apiEndpoint = import.meta.env.NG_APP_API_URL;
    deleteCriteriaResponseObservable = new Subject<any>();
    isWorkbenchChanged = false;
    nextBtnToolTipMessage = '';
    onScrollEvent = new Subject();
    saveWorkbenchSubject = new Subject();
    screenType = '';
    public workbench_id = '0';
    constructor(private readonly http: HttpClient) {}

    deepCompare(arg1: any, arg2: any): boolean {
        if (
            Object.prototype.toString.call(arg1) ===
            Object.prototype.toString.call(arg2)
        ) {
            if (
                Object.prototype.toString.call(arg1) === '[object Object]' ||
                Object.prototype.toString.call(arg1) === '[object Array]'
            ) {
                if (Object.keys(arg1).length !== Object.keys(arg2).length) {
                    return false;
                }
                return Object.keys(arg1).every((key) => {
                    return this.deepCompare(arg1[key], arg2[key]);
                });
            }
            return arg1 === arg2;
        }
        return false;
    }

    deleteCriteriaAPICAll(
        workbench_id: number,
        instance_id: string,
        criteria_type: string,
        criteria_id: number,
        updated: string
    ) {
        this.inclusivityDeleteCriteria(
            workbench_id,
            instance_id,
            criteria_type,
            criteria_id,
            updated
        ).subscribe({
            next: (res) => {
                this.deleteCriteriaResponseObservable.next({
                    criteria_id,
                    criteria_type,
                    data: res.data,
                });
            },
            error: (err) => {
                this.deleteCriteriaResponseObservable.error(err);
            },
        });
    }

    deletePromptInstruction(
        promptId: string,
        instanceId: string,
        workbenchId: number
    ) {
        return this.http.delete(
            this.apiEndpoint +
                `${apiConstant.deletePromptInstructionLibrary}?prompt_id=${promptId}&instance_id=${instanceId}&workbench_id=${workbenchId}`
        );
    }

    exportHeatmap(
        instance_id: string,
        workbench_id: number
    ): Observable<exportHeatmapResponseModel> {
        const params = new HttpParams()
            .set('instance_id', instance_id)
            .set('workbench_id', workbench_id);
        return this.http.post<exportHeatmapResponseModel>(
            this.apiEndpoint + `${apiConstant.exportHeatmap}`,
            null,
            {
                params,
            }
        );
    }

    fetchAllPromptData(instanceId: string) {
        return this.http.post(
            this.apiEndpoint +
                `${apiConstant.fetchAllPromptInstruction}?instance_id=${instanceId}`,
            null
        );
    }

    fetchData(query: string, filterCriteria: string): Observable<any> {
        return this.http.get<any>(
            this.apiEndpoint +
                `${apiConstant.fetchStudyInfoSIL}?study_type_prefix_val=${query}&study_type=${filterCriteria}`
        );
    }

    fetchDocSection(doc_hash_id: string) {
        return this.http.get<fetchDocModel>(
            this.apiEndpoint + `${apiConstant.fetchDocSection}`,
            { params: { doc_hash_id } }
        );
    }

    fetchDraft(instance_id: string, workbench_id: number) {
        const params = new HttpParams()
            .set('instance_id', instance_id)
            .set('workbench_id', workbench_id);
        return this.http.post<fetchDraftResponseModel>(
            this.apiEndpoint + apiConstant.fetchDraft,
            null,
            { params }
        );
    }

    fetchPromptData(
        instanceId: string,
        workbenchId: number
    ): Observable<instructionsPromptResponseData> {
        return this.http.get<instructionsPromptResponseData>(
            this.apiEndpoint +
                `${apiConstant.fetchPromptInstruction}?instance_id=${instanceId}&workbench_id=${workbenchId}`
        );
    }

    fetchUploadedPDF() {
        return this.http.get<uploadPdfModel>(
            this.apiEndpoint + `${apiConstant.fetchUploadPDF}`
        );
    }

    generateDraft(
        payload: generateDraftPayloadType
    ): Observable<generateDraftResponseModel> {
        return this.http.post<generateDraftResponseModel>(
            this.apiEndpoint + apiConstant.generateDraft,
            payload
        );
    }

    generateHeatmap(
        instance_id: string,
        workbench_id: number,
        timestamp: string,
        regenerate_heatmap = true
    ): Observable<generateHeatmapResponseModel> {
        const params = new HttpParams()
            .set('instance_id', instance_id)
            .set('workbench_id', workbench_id)
            .set('regenerate_heatmap', regenerate_heatmap)
            .set('updated_on', timestamp);
        return this.http.post<generateHeatmapResponseModel>(
            this.apiEndpoint + apiConstant.generateHeatmap,
            null,
            { params }
        );
    }

    getNextBtnTooltipMessage(): string {
        return this.nextBtnToolTipMessage;
    }

    getSelectedStudy(studyId: string) {
        return this.http.get(
            this.apiEndpoint +
                `${apiConstant.fetchStudyDetailFromSIL}?study_id=${studyId}`
        );
    }

    inclusivityDeleteCriteria(
        workbench_id: number,
        instance_id: string,
        criteria_type: string,
        criteria_id: number,
        updated: string
    ) {
        const payload = {
            criteria_id,
            criteria_type,
            instance_id,
            workbench_id,
            updated_on: updated,
        };

        return this.http.delete<generateDraftResponseModel>(
            this.apiEndpoint + `${apiConstant.inclusivityDeleteCriteria}`,
            { body: payload }
        );
    }

    inclusivityRearrangeCritiria(
        workbench_id: number,
        instance_id: string,
        criteria_type: string,
        criteria_data: any[]
    ) {
        return this.http.post(
            this.apiEndpoint + apiConstant.inclusivityRearrangeCriteria,
            { criteria_data, criteria_type, instance_id, workbench_id }
        );
    }

    inclusivitySaveCriteria(
        workbench_id: number,
        instance_id: string,
        criteria_type: string,
        criteria_data: Omit<criteriaModel, 'type'>,
        updated_on: string
    ) {
        return this.http.post(
            this.apiEndpoint + apiConstant.inclusivitySaveCriteria,
            {
                criteria_data,
                criteria_type,
                instance_id,
                workbench_id,
                updated_on,
            }
        );
    }

    inclusivitySaveRationale(
        workbench_id: number,
        instance_id: string,
        criteria_rationale_data: any
    ): Observable<any> {
        return this.http.post<any>(
            this.apiEndpoint + apiConstant.inclusivitySaveRationale,
            { criteria_rationale_data, instance_id, workbench_id }
        );
    }

    inclusivityUpdateRecommendationDecision(
        criteria_id: number,
        recommendation_id: number,
        decision_type: string,
        decision_rationale_text: string,
        instance_id: string,
        updated_on: string
    ) {
        return this.http.post(
            this.apiEndpoint +
                apiConstant.inclusivityUpdateCriteriaRecommendationDecision,
            {
                criteria_id,
                decision_rationale_text,
                decision_type,
                instance_id,
                recommendation_id,
                updated_on,
            }
        );
    }

    regenerateDraft(
        instance_id: string,
        workbench_id: number,
        include_category_flag = true
    ): Observable<generateDraftResponseModel> {
        const params = new HttpParams()
            .set('instance_id', instance_id)
            .set('workbench_id', workbench_id)
            .set('include_category_flag', include_category_flag);
        return this.http.post<generateDraftResponseModel>(
            this.apiEndpoint + apiConstant.regenerateDraft,
            null,
            { params }
        );
    }

    regenerateHeatmap(
        payload: regenerateHeatmapPayloadModel
    ): Observable<regenerateHeatmapResponseModel> {
        return this.http.post<regenerateHeatmapResponseModel>(
            this.apiEndpoint + apiConstant.regenerateHeatmap,
            payload
        );
    }

    regenerateHeatmapOnReassignment(
        payload: regenerateHeatmapOnReassignmentPayloadModel
    ): Observable<regenerateHeatmapResponseModel> {
        return this.http.post<regenerateHeatmapResponseModel>(
            this.apiEndpoint + apiConstant.regenerateHeatmapOnReassignment,
            payload
        );
    }

    resetECWorkbenchPromptInstruction(
        instanceId: string,
        workbenchId: number,
        promptId: number,
        updated_on: string
    ): Observable<resetECPromptInstructionResponseModel> {
        return this.http.get<resetECPromptInstructionResponseModel>(
            this.apiEndpoint +
                `${apiConstant.resetECWorkbenchPromptInstruction}?instance_id=${instanceId}&workbench_id=${workbenchId}&prompt_id=${promptId}&updated_on=${updated_on}`
        );
    }

    restoreOriginalDraft(
        instance_id: string,
        workbench_id: number
    ): Observable<resetDraftResponseModel> {
        const params = new HttpParams()
            .set('instance_id', instance_id)
            .set('workbench_id', workbench_id);
        return this.http.post<resetDraftResponseModel>(
            this.apiEndpoint + apiConstant.restoreOriginalDraft,
            null,
            { params }
        );
    }

    restoreOriginalHeatmap(
        instance_id: string,
        workbench_id: number,
        updated: string
    ): Observable<restoreOriginalHeatmapResponseModel> {
        const params = new HttpParams()
            .set('instance_id', instance_id)
            .set('workbench_id', workbench_id)
            .set('updated_on', updated);
        return this.http.post<restoreOriginalHeatmapResponseModel>(
            this.apiEndpoint + apiConstant.restoreOriginalHeatmap,
            null,
            { params }
        );
    }

    saveDraft(
        payload: saveDraftPayloadModel
    ): Observable<saveDraftResponseModel> {
        return this.http.post<saveDraftResponseModel>(
            this.apiEndpoint + apiConstant.saveECDraft,
            payload
        );
    }

    saveHeatmap(payload: saveHeatmapPayloadType) {
        return this.http.post(
            this.apiEndpoint + apiConstant.saveHeatmap,
            payload
        );
    }

    savePromptData(payload: savePromptInstructionPayloadModel) {
        return this.http.post(
            this.apiEndpoint + apiConstant.savePromptInstruction,
            payload
        );
    }

    savePromptInstructionLibraryData(
        payload: saveInstructionLibraryPayloadModel
    ): Observable<savePromptInstructionLibraryDataResponseModel> {
        return this.http.post<savePromptInstructionLibraryDataResponseModel>(
            this.apiEndpoint + apiConstant.savePromptInstructionLibrary,
            payload
        );
    }

    saveWorkbenchInput(
        payload: StudyPayloadModel
    ): Observable<saveWorkbenchInputStudyResponseModel> {
        return this.http.post<saveWorkbenchInputStudyResponseModel>(
            this.apiEndpoint + apiConstant.saveWorkbenchStudyInput,
            payload
        );
    }

    setData(arg0: {
        constructor: Function;
        hasOwnProperty(v: PropertyKey): boolean;
        isPrototypeOf(v: Object): boolean;
        project_name: string;
        propertyIsEnumerable(v: PropertyKey): boolean;
        toLocaleString(): string;
        toString(): string;
        valueOf(): Object;
    }) {
        throw new Error('Method not implemented.');
    }

    setNextBtnTooltipMessage(message: string) {
        this.nextBtnToolTipMessage = message;
    }

    deleteECBCommentById(payload: DeleteECBCommentByIdPayload): Observable<any> {
        return this.http.post(
            this.apiEndpoint + apiConstant.deleteECBCommentById,
            payload
        );
    }

    editECBComment(payload: EditECBCommentPayload): Observable<any> {
        return this.http.post(
            this.apiEndpoint + apiConstant.editECBComment,
            payload
        );
    }

    saveECBComment(payload: SaveECBCommentPayload): Observable<any> {
        return this.http.post(
            this.apiEndpoint + apiConstant.saveECBComment,
            payload
        );
    }

    resolveECBComment(payload: ResolveECBCommentPayload): Observable<any> {
        const queryString = buildQueryString(payload);
        return this.http.post(
            `${this.apiEndpoint}${apiConstant.resolveECBComment}?${queryString}`,
            {}
        );
    }
}
